import { useEffect, useState } from 'react'
import QRCode from 'qrcode'

export interface QrCodeProps {
  /** The URL to encode. */
  value: string
  /** Rendered size in CSS pixels. */
  size?: number
}

/**
 * A QR code rendered locally.
 *
 * Not a third-party image service: handing your room's URL to somebody else's
 * server is unnecessary, and it would stop working exactly when it is needed
 * most — a house with wobbly broadband and a television full of children.
 */
export function QrCode({ value, size = 240 }: QrCodeProps) {
  const [svg, setSvg] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    void QRCode.toString(value, {
      type: 'svg',
      margin: 1,
      // High correction, because this gets photographed from across a room at
      // an angle, often by a phone with a smeared lens.
      errorCorrectionLevel: 'H',
      color: { dark: '#000000', light: '#ffffff' },
    })
      .then((markup) => {
        if (!cancelled) setSvg(markup)
      })
      .catch(() => {
        if (!cancelled) setSvg(null)
      })

    return () => {
      cancelled = true
    }
  }, [value])

  if (!svg) {
    return (
      <div
        style={{ width: size, height: size }}
        className="bg-surface-sunken rounded-(--radius-control)"
        aria-hidden="true"
      />
    )
  }

  return (
    <div
      style={{ width: size, height: size }}
      className="overflow-hidden rounded-(--radius-control) bg-white p-2 [&>svg]:size-full"
      // The code itself is decorative; the room code beside it is the real
      // instruction, and it is announced as text.
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}
